import React from 'react';
import { motion } from 'framer-motion';
import { studioData, studioAwards } from '../data/portfolioData';
import { Award, Compass, Globe, Sparkles } from 'lucide-react';

export const AboutStudio: React.FC = () => {
  return (
    <section id="studio" className="py-24 px-6 md:px-12 max-w-7xl mx-auto relative z-10 border-t border-white/10 scroll-mt-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
        {/* Left Column: Architect Portrait & Monogram */}
        <div className="lg:col-span-5 space-y-6">
          <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-white/15 bg-arch-card">
            {/* Architect Portrait Image */}
            <img
              src={studioData.portraitImage || "/assets/profile.jpg"}
              alt={studioData.founder}
              className="w-full h-full object-cover object-center transition-all duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-transparent to-transparent opacity-80" />

            {/* Corner Drafting Marks */}
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded bg-[#0A0B0D]/80 backdrop-blur border border-white/10 text-[10px] font-mono text-arch-accent uppercase">
              PRINCIPAL ARCHITECT
            </div>

            <div className="absolute bottom-6 left-6 right-6 text-white space-y-1">
              <h3 className="text-2xl font-serif font-medium">{studioData.founder}</h3>
              <p className="text-xs font-mono text-neutral-400">{studioData.title}</p>
              <div className="pt-2 flex items-center gap-2 text-[10px] font-mono text-arch-accent">
                <Globe className="w-3 h-3" />
                <span>Licensed UAP Architect • Metro Manila & Worldwide</span>
              </div>
            </div>
          </div>

          <div className="p-5 rounded-sm bg-white/[0.02] border border-white/5 space-y-2 text-xs font-mono">
            <div className="text-arch-muted uppercase text-[10px]">Studio Coordinates</div>
            <div className="text-white">{studioData.coordinates}</div>
            <div className="text-neutral-400 pt-1">
              Studio hours: Mon — Fri, 09:00 — 18:00 PHT (UTC+8)
            </div>
          </div>
        </div>

        {/* Right Column: Statement, Philosophy & Awards */}
        <div className="lg:col-span-7 space-y-12">
          {/* Philosophy Text */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 text-xs font-mono text-arch-accent uppercase tracking-widest">
              <Compass className="w-3.5 h-3.5" />
              <span>05 // Philosophy & Practice</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-serif text-white leading-tight">
              Crafting architecture rooted in <span className="italic font-normal text-arch-limestone">climate</span>, material honesty, and spatial permanence.
            </h2>

            <div className="space-y-4 text-sm sm:text-base text-neutral-300 font-light leading-relaxed">
              {studioData.philosophyFull.map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </div>

          {/* Awards & Citations */}
          <div className="space-y-6 pt-6 border-t border-white/10">
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono text-arch-accent uppercase tracking-widest flex items-center gap-2">
                <Award className="w-3.5 h-3.5" />
                <span>Selected Honors & Awards</span>
              </div>
              <span className="text-[10px] font-mono text-neutral-500">2021 — 2024</span>
            </div>

            <div className="divide-y divide-white/5">
              {studioAwards.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-xs font-mono"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-arch-accent font-semibold">{item.year}</span>
                    <span className="text-white">{item.award}</span>
                  </div>
                  <div className="text-neutral-400 text-[11px] sm:text-right">
                    {item.project} • <span className="text-arch-muted">{item.category}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Sustainability Badge */}
          <div className="p-6 rounded-sm bg-arch-surface/60 border border-white/10 flex items-start gap-4">
            <div className="w-9 h-9 rounded bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 flex-shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="space-y-1">
              <h4 className="text-sm font-serif text-white font-medium">
                Passive Solar & Tropical Sustainability Commitment
              </h4>
              <p className="text-xs text-neutral-400 font-light leading-relaxed">
                Every residential and commercial design maximizes natural cross-ventilation, rainwater catchment, daylight factor optimization, and low-embodied carbon timber/concrete mixtures.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
